import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  LinearProgress,
  Avatar,
  useMediaQuery,
  useTheme
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { StyledIconComponent } from "../../../../components/BookingForm/partial/form-selection.partial";

type AdvancedPerTypeAccordionProps = {
  title: string;
  typeIcon: React.ReactNode;
  defaultExpanded?: boolean;
  rowTestId?: string;
  contents: JSX.Element;
  children: React.ReactNode;
};

export const AdvancedPerTypeAccordion = ({
  title,
  typeIcon,
  defaultExpanded = false,
  rowTestId,
  contents,
  children
}: AdvancedPerTypeAccordionProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ boxShadow: "none" }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: "center" }}>
        <AdvancedBookingPerTypeRow
          icon={typeIcon}
          label={title}
          contents={contents}
          testId={rowTestId}
        />
      </AccordionSummary>

      <AccordionDetails sx={[isMobile ? { p: "4px 8px 16px 8px" } : { p: "4px 8px 16px 32px" }]}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

type AdvancedBookingPerTypeRowProps = {
  icon: React.ReactNode;
  label: string;
  contents: JSX.Element;
  testId?: string;
};

export const AdvancedBookingPerTypeRow = ({
  icon,
  label,
  contents,
  testId
}: AdvancedBookingPerTypeRowProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      data-testid={testId ?? `AdvancedBookingPerTypeRow-${label}`}
      sx={{
        display: "grid",
        gridTemplateColumns: "40px 1fr",
        columnGap: 2,
        alignItems: "flex-start",
        width: "97%"
      }}
    >
      {/* Icon */}
      <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>{icon}</Box>

      {/* Content */}
      <Box sx={{ alignSelf: "center" }}>
        {/* Top row: name + weeks */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography fontWeight={500}>{label}</Typography>

          {!isMobile && (
            <Typography variant="body2" color="text.secondary">
              {contents}
            </Typography>
          )}
        </Box>

        {/* LinearProgress */}
        <LinearProgress variant="determinate" value={100} />
      </Box>
    </Box>
  );
};

export const AdvancedBookingPerTypeRowIcon = ({ icon }: { icon: React.ReactNode }) => {
  return (
    <Avatar>
      <StyledIconComponent iconSize={{ width: "18px", height: "18px" }} sx={{ marginBottom: 0 }}>
        {icon}
      </StyledIconComponent>
    </Avatar>
  );
};
